import useEdgeInfiniteQuery from '@app/data/edge/hooks/useEdgeInfiniteQuery';
import GET_INBOX_MESSAGES from '../queries/GetInboxMessages';
import type { InboxMessageFields } from '../types';

interface InboxPageInfo {
  endCursor: string;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string;
  totalCount: number;
}

interface InboxResponse {
  viewer: {
    inbox: {
      edges: InboxMessageFields[];
      pageInfo: InboxPageInfo;
    };
  };
}

interface Options {
  deviceId: string;
  first: number;
  after?: string;
  sort?: string;
}

/**
 * Get all inbox messages for user from edge layer
 * Sort default is ascending
 */
const useInboxMessages = (
  deviceId: string,
  first: number,
  enabled = true,
  after?: string,
  sort?: string,
) => {
  const { data, isLoading, error, fetchNextPage, isFetchingNextPage } =
    useEdgeInfiniteQuery<InboxResponse, Options>(GET_INBOX_MESSAGES, {
      variables: {
        deviceId,
        first,
        after,
        sort,
      },
      enabled,
      numberOfItems: 35,
      getNextPageParam: (lastPage) => {
        // lastPage is the prev data
        const pageInfo = lastPage?.viewer?.inbox?.pageInfo;
        if (!pageInfo || !pageInfo?.hasNextPage) {
          return '';
        }

        return pageInfo.endCursor;
      },
    });

  const inbox = data?.pages?.map((page) => page?.viewer?.inbox);

  return {
    loading: isLoading,
    error,
    messages: inbox?.map(({ edges }) => edges).flat() ?? [],
    pageInfo: inbox?.slice(-1)[0]?.pageInfo ?? ({} as InboxPageInfo),
    fetchNextPage,
    isFetchingNextPage,
  };
};

export default useInboxMessages;
